/**
 * ReSettlement API
 * 재마감 관련 API
 */
import { apiClient } from "@/shared/api/client";
import {
    ReSettlementTableOption,
    ReSettlementRequest,
    ReSettlementResponse,
    ReSettlementSupplier,
    ReSettlementRider,
} from "../types/reSettlement.types";
import { extractArrayFromResponse, toSafeString } from "@/shared/utils/apiResponseHelpers";

const BASE_PATH = '/api/v1/system/re-settlement';
const DELIVERY_BASE_PATH = '/api/v1/delivery';

/** API 응답 타입 (tables 속성 포함) */
type GetTablesResponse = {
    tables?: ReSettlementTableOption[];
    data?: ReSettlementTableOption[];
    items?: ReSettlementTableOption[];
} | ReSettlementTableOption[];

export const reSettlementApi = {
    /**
     * 재마감 테이블 목록 조회
     * GET /api/v1/system/re-settlement/tables
     */
    getTables: async (): Promise<ReSettlementTableOption[]> => {
        const response = await apiClient.get<GetTablesResponse>(
            `${BASE_PATH}/tables`
        );
        const data = response.data;
        
        // 배열이면 그대로 반환
        if (Array.isArray(data)) {
            return data;
        }
        
        // 객체인 경우 tables, data, items 속성 확인
        if (data && typeof data === 'object') {
            if (Array.isArray(data.tables)) {
                return data.tables;
            }
            if (Array.isArray(data.data)) {
                return data.data;
            }
            if (Array.isArray(data.items)) {
                return data.items;
            }
        }
        
        return [];
    },

    /**
     * 재마감 적용
     * POST /api/v1/system/re-settlement
     */
    applyReSettlement: async (request: ReSettlementRequest): Promise<ReSettlementResponse> => {
        const response = await apiClient.post<ReSettlementResponse>(
            BASE_PATH,
            request,
            {
                timeout: 300000,
            }
        );
        
        // 응답이 없거나 success 필드가 없으면 HTTP 상태 코드로 판단
        // (백엔드가 비동기로 처리하고 즉시 응답을 반환하는 경우)
        const data = response.data;
        if (!data || data.success === undefined) {
            return {
                success: response.status === 200,
                message: data?.message
            };
        }
        
        return data;
    },

    /**
     * 협력사 목록 조회
     *
     * GET /api/v1/delivery/supplier/list
     */
    getSuppliers: async (): Promise<ReSettlementSupplier[]> => {
        type SupplierRawItem = { suppId?: string; suppName?: string };
        type SupplierListResponse = SupplierRawItem[] | { suppliers?: SupplierRawItem[]; items?: SupplierRawItem[]; data?: SupplierRawItem[] };

        const response = await apiClient.get<SupplierListResponse>(
            `${DELIVERY_BASE_PATH}/supplier/list`
        );

        const items = extractArrayFromResponse<SupplierRawItem>(
            response.data as SupplierRawItem[] | { suppliers?: SupplierRawItem[]; items?: SupplierRawItem[]; data?: SupplierRawItem[] },
            'suppliers'
        );

        return items
            .map((item) => ({
                suppId: toSafeString(item.suppId),
                suppName: item.suppName ?? '',
            }))
            .filter((item) => item.suppId && item.suppName);
    },

    /**
     * 라이더 목록 조회
     *
     * GET /api/v1/delivery/rider/list
     * @param suppId 협력사 ID
     */
    getRiders: async (suppId: string): Promise<ReSettlementRider[]> => {
        if (!suppId) {
            return [];
        }

        type RiderRawItem = { riderId?: string; riderName?: string };
        type RiderListResponse = RiderRawItem[] | { riders?: RiderRawItem[]; items?: RiderRawItem[]; data?: RiderRawItem[] };

        const response = await apiClient.get<RiderListResponse>(
            `${DELIVERY_BASE_PATH}/rider/list`,
            { params: { suppId } }
        );

        const items = extractArrayFromResponse<RiderRawItem>(
            response.data as RiderRawItem[] | { riders?: RiderRawItem[]; items?: RiderRawItem[]; data?: RiderRawItem[] },
            'riders'
        );

        return items
            .map((item) => ({
                riderId: toSafeString(item.riderId),
                riderName: item.riderName ?? '',
            }))
            .filter((item) => item.riderId && item.riderName);
    },
};

